import React, { FC, Fragment, ReactNode } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
  activeCategoryCount,
  isCiaCategory,
  ModelFilterCategory,
  ModelFilterState,
  useModelFilterActions,
  useModelFilters,
  useShareFilters,
} from '@store/zustand/model-filters';
import {
  CATEGORY_GROUPS,
  CATEGORY_LABELS,
} from '@views/Project/components/ModelFiltersPopover/categories';
import FilterCategoryRow from '@views/Project/components/ModelFiltersPopover/components/FilterCategoryRow';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';
import ShareFiltersPill from '@views/Project/components/ModelFiltersPopover/components/ShareFiltersPill';

type Props = {
  onOpenCategory: (category: ModelFilterCategory) => void;
  availability: Partial<Record<ModelFilterCategory, boolean>>;
  titles?: Record<ModelFilterCategory, string>;
  labels: Partial<Record<ModelFilterCategory, Record<string, string>>>;
  icons: Partial<Record<ModelFilterCategory, ReactNode>>;
  canShareFilters?: boolean;
};

/**
 * The values a category row summarises. A CIA category's selection is spread
 * over its criteria, so the row sees every criteria's answers as one list.
 */
const selectionFor = (
  state: ModelFilterState,
  category: ModelFilterCategory,
): string[] =>
  isCiaCategory(category)
    ? Object.values(state.ciaFilters[category]).flat()
    : state[category];

/**
 * The Filters root: a header and the AC3.1 category list, grouped by
 * dividers.
 *
 * It renders no values of its own, and names each row by the title the
 * container resolved — a CIA category with one criteria goes by that
 * criteria's name — falling back to the default labels when handed none. A
 * row click hands the category upwards and the container swaps this panel
 * for the category panel in the same popover, which is what AC4.1.1 means by
 * "no second popover". A category whose `availability` is `false` is left
 * out together with its divider when it was the last of its group (AC3.1.1
 * to AC3.1.3); the data behind that decision is resolved by the container,
 * so this panel fetches nothing. "Clear all" appears only while some
 * category carries a selection (AC2.2) and empties every category at once;
 * it leaves the Share filters pill as it is. The pill sits between the title
 * and Clear all, after a short rule, when the container says the tier offers
 * both modes to share between; it reads and writes the store's flag
 * directly, the way Clear all does. Its rule is a plain box rather than a
 * `Divider`, so the header adds no `hr` to the list's group dividers.
 */
const FiltersRootPanel: FC<Props> = ({
  onOpenCategory,
  availability,
  titles = CATEGORY_LABELS,
  labels,
  icons,
  canShareFilters = false,
}) => {
  const state = useModelFilters();
  const shareFilters = useShareFilters();
  const { clearAll, setShareFilters } = useModelFilterActions();

  const visibleGroups = CATEGORY_GROUPS.map((group) =>
    group.filter((category) => availability[category] !== false),
  ).filter((group) => group.length > 0);

  return (
    <FilterPanelLayout
      title={
        <Stack direction="row" alignItems="center" gap="8px" minWidth={0}>
          <Typography variant="h5" color="grey.1000">
            Filters
          </Typography>

          {canShareFilters && (
            <>
              <Box
                aria-hidden
                sx={{
                  width: '1px',
                  height: 16,
                  bgcolor: 'grey.500',
                  flexShrink: 0,
                }}
              />

              <ShareFiltersPill
                checked={shareFilters}
                onChange={setShareFilters}
              />
            </>
          )}
        </Stack>
      }
      action={
        activeCategoryCount(state) > 0 && (
          <UnderlineTextButton
            $disabled={false}
            onClick={clearAll}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {visibleGroups.map((group, index) => (
        <Fragment key={group.join('-')}>
          {index > 0 && <Divider sx={{ borderColor: 'grey.500' }} />}

          {group.map((category) => (
            <FilterCategoryRow
              key={category}
              icon={icons[category]}
              label={titles[category]}
              values={selectionFor(state, category)}
              labels={labels[category] ?? {}}
              onClick={() => onOpenCategory(category)}
            />
          ))}
        </Fragment>
      ))}
    </FilterPanelLayout>
  );
};

export default FiltersRootPanel;
